import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/services/federal')({
  component: FederalPage,
  head: () => ({
    meta: [
      { title: 'Federal services — Centrelink, Medicare, DVA, NDIS — Warren-Blackwood' },
      {
        name: 'description',
        content:
          'Federal matters your state MP cannot act on directly. Contacts for Centrelink, Medicare, Services Australia, DVA, NDIS, and the federal members for Forrest and O\u2019Connor who cover Warren-Blackwood.'
      },
      { property: 'og:title', content: 'Federal services — Warren-Blackwood Electorate Guide' },
      {
        property: 'og:description',
        content:
          'Plain-English handoff: which federal agency to call, and how to reach the federal members for Forrest and O\u2019Connor.'
      },
    ],
  }),
});

type Phone = { label: string; tel: string; note?: string };
type Block = {
  agency: string;
  what: string;
  phone?: Phone[];
  website?: { label: string; href: string };
  tips?: string[];
};

const BLOCKS: { id: string; title: string; blurb: string; items: Block[] }[] = [
  {
    id: 'centrelink',
    title: 'Centrelink — income support',
    blurb:
      'Services Australia delivers Centrelink payments. Different payments have different phone lines — use the right one to skip queues.',
    items: [
      {
        agency: 'Centrelink — Employment Services',
        what: 'JobSeeker, Youth Allowance (jobseekers), Parenting Payment.',
        phone: [{ label: 'Employment services', tel: '132850', note: '132 850' }],
        website: { label: 'servicesaustralia.gov.au', href: 'https://www.servicesaustralia.gov.au/centrelink' },
      },
      {
        agency: 'Centrelink — Disability, Sickness & Carers',
        what: 'Disability Support Pension, Carer Payment, Carer Allowance.',
        phone: [{ label: 'Disability and carers', tel: '132717', note: '132 717' }],
      },
      {
        agency: 'Centrelink — Families',
        what: 'Family Tax Benefit, Parental Leave Pay, Child Care Subsidy.',
        phone: [{ label: 'Families', tel: '136150', note: '136 150' }],
      },
      {
        agency: 'Centrelink — Older Australians',
        what: 'Age Pension, Pension Bonus, Commonwealth Seniors Health Card.',
        phone: [{ label: 'Older Australians', tel: '132300', note: '132 300' }],
      },
      {
        agency: 'Centrelink — General / multilingual',
        what: 'General Centrelink switchboard if you\u2019re not sure which line.',
        phone: [
          { label: 'General Centrelink', tel: '132300', note: '132 300' },
          { label: 'Multilingual phone service', tel: '131202', note: '131 202' },
          { label: 'Translating & Interpreting Service', tel: '131450', note: '131 450' },
        ],
      },
    ],
  },
  {
    id: 'medicare',
    title: 'Medicare & PBS',
    blurb:
      'Medicare rebates, the Pharmaceutical Benefits Scheme, and My Health Record are all federal.',
    items: [
      {
        agency: 'Medicare',
        what: 'Cards, claims, safety net, enrolling a baby, replacing a lost card.',
        phone: [{ label: 'General', tel: '132011', note: '132 011' }],
        website: { label: 'servicesaustralia.gov.au/medicare', href: 'https://www.servicesaustralia.gov.au/medicare' },
      },
      {
        agency: 'Pharmaceutical Benefits Scheme (PBS)',
        what: 'Subsidised medicines, safety net thresholds.',
        phone: [{ label: 'PBS information', tel: '1800020613', note: '1800 020 613' }],
        website: { label: 'pbs.gov.au', href: 'https://www.pbs.gov.au/' },
      },
      {
        agency: 'My Health Record',
        what: 'View your records, set access controls.',
        phone: [{ label: 'Help line', tel: '1800723471', note: '1800 723 471' }],
        website: { label: 'myhealthrecord.gov.au', href: 'https://www.myhealthrecord.gov.au/' },
      },
    ],
  },
  {
    id: 'service-centres',
    title: 'Services Australia — rural service centres',
    blurb:
      'There is no full Centrelink/Medicare office inside Warren-Blackwood. The nearest service centres are in Bunbury, Albany and Busselton; agent and access-point services operate in several towns for basic transactions.',
    items: [
      {
        agency: 'Bunbury Service Centre',
        what: 'Full Centrelink, Medicare and Child Support service.',
        website: { label: 'Find a service centre', href: 'https://findus.servicesaustralia.gov.au/' },
      },
      {
        agency: 'Albany Service Centre',
        what: 'Full Centrelink, Medicare and Child Support service \u2014 nearest for Denmark residents.',
        website: { label: 'Find a service centre', href: 'https://findus.servicesaustralia.gov.au/' },
      },
      {
        agency: 'Centrelink agents & access points',
        what: 'Community Resource Centres in Manjimup, Bridgetown, Pemberton, Nannup, Boyup Brook and Walpole offer phone, fax, internet and basic Centrelink agent services. They cannot make decisions on your claim.',
        website: { label: 'Find an agent or access point', href: 'https://findus.servicesaustralia.gov.au/' },
      },
    ],
  },
  {
    id: 'dva',
    title: 'Veterans \u2014 DVA & Open Arms',
    blurb:
      'The Department of Veterans\u2019 Affairs administers compensation, health cards and pensions for serving and ex-serving members and their families.',
    items: [
      {
        agency: 'Department of Veterans\u2019 Affairs (DVA)',
        what: 'Claims, Gold/White Card, income support, advocacy referrals.',
        phone: [{ label: 'General enquiries', tel: '1800838372', note: '1800 VETERAN (1800 838 372)' }],
        website: { label: 'dva.gov.au', href: 'https://www.dva.gov.au/' },
      },
      {
        agency: 'Open Arms \u2014 Veterans & Families Counselling',
        what: 'Free, confidential counselling for current and former ADF members and their families.',
        phone: [{ label: '24/7', tel: '1800011046', note: '1800 011 046' }],
        website: { label: 'openarms.gov.au', href: 'https://www.openarms.gov.au/' },
      },
      {
        agency: 'RSL WA \u2014 local sub-branches',
        what: 'Welfare and advocacy support; sub-branches in Margaret River, Bridgetown, Manjimup, Pemberton, Denmark.',
        website: { label: 'rslwa.org.au', href: 'https://rslwa.org.au/' },
      },
    ],
  },
  {
    id: 'ndis',
    title: 'NDIS \u2014 disability supports',
    blurb:
      'The National Disability Insurance Scheme is federal. The local Partner in the Community delivers planning and Early Childhood services on the NDIA\u2019s behalf.',
    items: [
      {
        agency: 'National Disability Insurance Agency (NDIA)',
        what: 'Eligibility, planning, plan reviews, payments.',
        phone: [{ label: 'NDIS contact centre', tel: '1800800110', note: '1800 800 110' }],
        website: { label: 'ndis.gov.au', href: 'https://www.ndis.gov.au/' },
      },
      {
        agency: 'NDIS Quality and Safeguards Commission',
        what: 'Complaints about an NDIS provider or worker.',
        phone: [{ label: 'Complaints line', tel: '1800035544', note: '1800 035 544' }],
        website: { label: 'ndiscommission.gov.au', href: 'https://www.ndiscommission.gov.au/' },
      },
      {
        agency: 'See also',
        what: 'The dedicated NDIS page on this site walks through the local Partner, ECEI, and how to prepare for a planning meeting.',
        website: { label: '/services/ndis', href: '/services/ndis' },
      },
    ],
  },
  {
    id: 'other',
    title: 'Other common federal contacts',
    blurb: 'A short list of federal lines people in this electorate often need.',
    items: [
      {
        agency: 'Australian Taxation Office (ATO)',
        what: 'Tax returns, BAS, individual and small business enquiries.',
        phone: [
          { label: 'Individuals', tel: '132861', note: '13 28 61' },
          { label: 'Small business', tel: '132866', note: '13 28 66' },
        ],
        website: { label: 'ato.gov.au', href: 'https://www.ato.gov.au/' },
      },
      {
        agency: 'Child Support',
        what: 'Assessments, collection, changes of assessment.',
        phone: [{ label: 'Child Support', tel: '131272', note: '131 272' }],
      },
      {
        agency: 'Australian Passports',
        what: 'New passports, renewals, urgent travel.',
        phone: [{ label: 'Information line', tel: '131232', note: '131 232' }],
        website: { label: 'passports.gov.au', href: 'https://www.passports.gov.au/' },
      },
      {
        agency: 'Australian Electoral Commission (federal enrolment)',
        what: 'Federal electoral roll \u2014 separate from the WA state roll.',
        phone: [{ label: 'AEC', tel: '132326', note: '13 23 26' }],
        website: { label: 'aec.gov.au', href: 'https://www.aec.gov.au/' },
      },
    ],
  },
];

function FederalPage() {
  const h2: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.4rem',
    color: 'var(--rivergum)',
    marginTop: 0,
    marginBottom: 'var(--sp-xs)',
    letterSpacing: '0.02em',
  };
  const p: React.CSSProperties = {
    color: 'var(--ink)',
    lineHeight: 1.6,
    marginBottom: 'var(--sp-sm)',
  };
  const link: React.CSSProperties = { color: 'var(--green-mid)', fontWeight: 600 };

  const card: React.CSSProperties = {
    background: 'var(--warm-white, #faf7f0)',
    border: '1px solid var(--light-border)',
    borderRadius: 10,
    padding: 'var(--sp-md)',
    marginBottom: 'var(--sp-md)',
  };
  const agencyBox: React.CSSProperties = {
    background: '#fff',
    border: '1px solid var(--light-border)',
    borderRadius: 8,
    padding: '0.75rem 0.9rem',
    marginTop: '0.6rem',
  };

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 820,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--green-mid)',
          margin: 0,
          marginBottom: '0.35rem',
        }}
      >
        Services · Federal matters
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5vw, 2.75rem)',
          color: 'var(--green)',
          margin: 0,
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.1,
        }}
      >
        Federal services
      </h1>
      <p
        style={{
          color: 'var(--ink-mid)',
          fontStyle: 'italic',
          fontSize: '1.05rem',
          marginBottom: 'var(--sp-lg)',
          lineHeight: 1.5,
        }}
      >
        These are federal matters. Your <em>state</em> MP can&apos;t act on
        them directly &mdash; but the right contacts are here, and your{' '}
        <em>federal</em> MP can chase them on your behalf.
      </p>

      <div
        style={{
          background: '#fdf6e3',
          border: '1px solid #e8d9a8',
          borderRadius: 8,
          padding: 'var(--sp-md)',
          marginBottom: 'var(--sp-lg)',
        }}
      >
        <strong style={{ color: '#7a5b1e' }}>State vs federal &mdash; quick test</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.95rem' }}>
          Does the payment, card or service come from <strong>Canberra</strong>{' '}
          (Centrelink, Medicare, NDIS, DVA, ATO, passports)? It&apos;s federal.
          Schools, hospitals, public housing, police, roads and water in WA are
          state &mdash; see{' '}
          <Link to="/services/who-do-i-call" style={link}>
            Who do I call?
          </Link>
        </p>
      </div>

      <section
        id="federal-member"
        style={{
          ...card,
          background: '#eef5fb',
          borderColor: '#b9d4e8',
        }}
      >
        <h2 style={{ ...h2, color: '#1b4f72' }}>
          Your federal member &mdash; Forrest or O&apos;Connor
        </h2>
        <p style={{ ...p, marginBottom: '0.4rem' }}>
          Warren-Blackwood is covered by two federal seats. The Shire of
          Augusta-Margaret River is in the seat of{' '}
          <strong>Forrest</strong> (member: Ben Small, Liberal). The shires of
          Nannup, Manjimup, Bridgetown-Greenbushes, Boyup Brook and Denmark are
          in the seat of <strong>O&apos;Connor</strong> (member: Rick Wilson,
          Liberal).
        </p>
        <div style={agencyBox}>
          <div style={{ fontWeight: 700, color: 'var(--ink)' }}>
            Office of the federal member for Forrest or O&apos;Connor
          </div>
          <div
            style={{
              color: 'var(--ink-mid)',
              fontSize: '0.95rem',
              margin: '0.2rem 0 0.4rem',
            }}
          >
            Contact details and electorate offices are listed on the Australian
            Parliament House website.
          </div>
          <div style={{ fontSize: '0.95rem' }}>
            <a
              href="https://www.aph.gov.au/Senators_and_Members"
              style={link}
            >
              Find the member for Forrest or O&apos;Connor &rarr;
            </a>
          </div>
        </div>
      </section>

      <nav
        aria-label="Topics on this page"
        style={{
          background: '#eef5fb',
          border: '1px solid #b9d4e8',
          borderRadius: 8,
          padding: 'var(--sp-md)',
          marginBottom: 'var(--sp-lg)',
        }}
      >
        <strong style={{ color: '#1b4f72' }}>Jump to a topic</strong>
        <ul
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            gap: '0.4rem 1rem',
            listStyle: 'none',
            padding: 0,
            margin: '0.6rem 0 0',
          }}
        >
          {BLOCKS.map((b) => (
            <li key={b.id}>
              <a href={`#${b.id}`} style={link}>
                {b.title.split('\u2014')[0].trim()}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {BLOCKS.map((topic) => (
        <section key={topic.id} id={topic.id} style={card}>
          <h2 style={h2}>{topic.title}</h2>
          <p style={{ ...p, marginBottom: 0 }}>{topic.blurb}</p>

          {topic.items.map((c, i) => (
            <div key={i} style={agencyBox}>
              <div
                style={{
                  fontWeight: 700,
                  color: 'var(--ink)',
                  marginBottom: '0.15rem',
                }}
              >
                {c.agency}
              </div>
              <div
                style={{
                  color: 'var(--ink-mid)',
                  fontSize: '0.95rem',
                  marginBottom: '0.4rem',
                  lineHeight: 1.5,
                }}
              >
                {c.what}
              </div>
              {c.phone && c.phone.length > 0 && (
                <div style={{ fontSize: '0.95rem', marginBottom: '0.25rem' }}>
                  {c.phone.map((ph, j) => (
                    <div key={j}>
                      <span style={{ color: 'var(--ink-mid)' }}>{ph.label}: </span>
                      <a href={`tel:${ph.tel}`} style={link}>
                        {ph.note ?? ph.tel}
                      </a>
                    </div>
                  ))}
                </div>
              )}
              {c.website && (
                <div style={{ fontSize: '0.95rem' }}>
                  <a href={c.website.href} style={link}>
                    {c.website.label} &rarr;
                  </a>
                </div>
              )}
              {c.tips && c.tips.length > 0 && (
                <ul
                  style={{
                    margin: '0.4rem 0 0',
                    paddingLeft: '1.1rem',
                    color: 'var(--ink-mid)',
                    fontSize: '0.9rem',
                    lineHeight: 1.5,
                  }}
                >
                  {c.tips.map((tip, k) => (
                    <li key={k}>{tip}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </section>
      ))}

      <div
        style={{
          background: '#fdf6e3',
          border: '1px solid #e8d9a8',
          borderRadius: 8,
          padding: 'var(--sp-md)',
          marginTop: 'var(--sp-lg)',
        }}
      >
        <strong style={{ color: '#7a5b1e' }}>Honest handoff</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.95rem' }}>
          If you&apos;ve tried the relevant federal line and can&apos;t get a
          resolution, your federal member&apos;s office (Forrest or O&apos;Connor)
          can chase it. For state-run services, see{' '}
          <Link to="/services/who-do-i-call" style={link}>
            Who do I call?
          </Link>{' '}
          or your <Link to="/contact" style={link}>state MP&apos;s office</Link>.
        </p>
      </div>
    </main>
  );
}
